import { useQuery } from '@apollo/client'

import { GetPureSupplierRelationshipsSelectionDetailedDocument } from '@/graphql/purchase-plus/generated/getPureSupplierRelationshipsSelectionDetailed.generated'
import {
  SortDirectionEnum,
  SuppliersQuerySortsPropertyEnum,
  TradeRelationshipStatusEnum,
} from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { PURCHASE_PLUS_GRAPHQL_API } from '@/modules/shared/constants'
import { checkNetworkStatus } from '@/modules/shared/utils/checkNetworkStatus'
import { extractEdgesWithType } from '@/modules/shared/utils/extractEdgesWithType'
import { ignoreSingleCharacter } from '@/modules/shared/utils/ignoreSingleCharacter'
import { createPaginationUtils } from '@/modules/shared/utils/paginationUtils'

interface UseGetPureSupplierRelationshipsSelectionDetailedProps {
  purchaserId: number | undefined
  accountsPayableSystemId?: number
  searchText?: string
  skip?: boolean
}

const paginationResultsPerPage = 20

export function useGetPureSupplierRelationshipsSelectionDetailed({
  purchaserId,
  accountsPayableSystemId,
  searchText,
  skip,
}: UseGetPureSupplierRelationshipsSelectionDetailedProps) {
  const { data, error, networkStatus, refetch, fetchMore } = useQuery(
    GetPureSupplierRelationshipsSelectionDetailedDocument,
    {
      variables: {
        first: paginationResultsPerPage,
        after: null,
        accountsPayableSystemId,
        filters: {
          searchTextFuzzy: ignoreSingleCharacter(searchText),
          statusIn: [TradeRelationshipStatusEnum.Mutual],
          purchaserIdEq: purchaserId,
          enabled: true,
        },
        sorts: [{ property: SuppliersQuerySortsPropertyEnum.Name, direction: SortDirectionEnum.Asc }],
      },
      context: { uri: PURCHASE_PLUS_GRAPHQL_API },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip,
    }
  )

  const pageInfo = data?.accountsPayableSystem?.suppliers?.pageInfo

  return {
    suppliers: extractEdgesWithType(data?.accountsPayableSystem?.suppliers),
    loadingStates: checkNetworkStatus(networkStatus),
    error,
    pageInfo,
    networkStatus,
    ...createPaginationUtils({ pageInfo, refetch, fetchMore, paginationResultsPerPage }),
  }
}
